class LpEditorialStickyBar extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.ticking = false;
  }

  connectedCallback() {
    this.bar = this.querySelector('.lp-editorial-sticky-bar');
    if (!this.bar) return;

    this.htmlEl = document.documentElement;
    this.hiddenClass = 'hidden';
    this.triggerSelector = this.dataset.triggerSelector || '#top';
    this.fallbackScroll = parseInt(this.dataset.fallbackScroll || '320', 10);
    this.heroEl = this.findHero();
    this.pageFooter = this.findFooter();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.onScroll());
      this.resizeObserver.observe(this.bar);
      if (this.pageFooter) {
        this.resizeObserver.observe(this.pageFooter);
      }
    }

    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.resizeObserver?.disconnect();
    this.bar = null;
    this.hideBar();
  }

  findHero() {
    const selector = this.triggerSelector.trim();
    if (!selector) return null;

    try {
      const match = document.querySelector(selector);
      if (match) return match;
    } catch (error) {
      // Invalid selector — fall through to defaults.
    }

    return (
      document.querySelector('#top') ||
      document.querySelector('[data-lp-editorial-sticky-hero]') ||
      document.querySelector('.lp-editorial-hero')
    );
  }

  findFooter() {
    return (
      document.querySelector('.footer--root') ||
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer')
    );
  }

  onScroll() {
    if (this.ticking) return;

    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.evaluate();
    });
  }

  hasHeroPassed() {
    if (!this.heroEl) {
      return window.scrollY >= this.fallbackScroll;
    }

    const rect = this.heroEl.getBoundingClientRect();
    return rect.bottom <= 0;
  }

  isFooterOverlapping() {
    if (!this.pageFooter) return false;

    const footerRect = this.pageFooter.getBoundingClientRect();
    const barHeight = this.bar.offsetHeight || 0;

    return footerRect.top <= window.innerHeight - barHeight;
  }

  evaluate() {
    if (!this.bar) return;

    if (!this.isEnabledForViewport()) {
      this.hideBar();
      return;
    }

    if (this.hasHeroPassed() && !this.isFooterOverlapping()) {
      this.showBar();
    } else {
      this.hideBar();
    }
  }

  isEnabledForViewport() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const showDesktop = this.bar.dataset.showDesktop === 'true';
    const showMobile = this.bar.dataset.showMobile === 'true';

    return isDesktop ? showDesktop : showMobile;
  }

  showBar() {
    if (!this.bar || !this.htmlEl) return;

    if (!this.bar.classList.contains(this.hiddenClass)) {
      this.setBarHeight();
      return;
    }

    this.bar.classList.remove(this.hiddenClass);
    this.htmlEl.classList.add('lp-editorial-sticky-bar-active');
    this.setBarHeight();
  }

  hideBar() {
    if (!this.bar || !this.htmlEl) return;

    if (!this.bar.classList.contains(this.hiddenClass)) {
      this.bar.classList.add(this.hiddenClass);
    }

    if (this.htmlEl.classList.contains('lp-editorial-sticky-bar-active')) {
      this.htmlEl.classList.remove('lp-editorial-sticky-bar-active');
    }

    this.htmlEl.style.removeProperty('--lp-editorial-sticky-bar-height');
  }

  setBarHeight() {
    if (!this.bar || !this.htmlEl) return;
    this.htmlEl.style.setProperty('--lp-editorial-sticky-bar-height', `${this.bar.offsetHeight}px`);
  }
}

if (!customElements.get('lp-editorial-sticky-bar-root')) {
  customElements.define('lp-editorial-sticky-bar-root', LpEditorialStickyBar);
}
